"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

import PetDetails from "@/components/PetDetails";
import ProductVariantSelection from "@/components/ProductVariantSelection";
import ProductCustomizationOptions from "@/components/ProductCustomizationOptions";
import ContactDetailsForm from "@/components/ContactDetailsForm";
import ConfirmationPage from "@/components/ConfirmationPage";
import { FunnelState, ProductVariant } from "@/hooks/useFunnelState";

interface FunnelStepRendererProps {
  funnelState: FunnelState;
  getLocalizedPath: (pathKey: string) => string;
  isLoadingPrice: boolean;
  fetchInitialBundlePrices: (
    petTypeArg: "cat" | "dog",
    petNameArg: string,
    breedArg: string | null,
    birthDateArg: Date | undefined,
    selectedBreedTypeArg: "purebred" | "crossbreed" | "mixed",
    genderArg: "male" | "female" | "unknown",
    isCastratedArg: boolean,
    isHouseCatArg: boolean,
    selectedDogSizeArg: "small" | "large" | null,
    preexistingCoverageArg: boolean,
    customerNameArg: string,
    customerEmailArg: string,
    locale: string,
    agentIdArg: string | null,
    worldwideCoverageArg: boolean,
    yearlyDeductibleArg: number,
    coPayCoveragePercentageArg: number,
    paymentIntervalArg: 1 | 2 | 4 | 12,
    paymentMethodArg: "credit_card" | "ESR",
    startOfInsuranceArg: Date
  ) => Promise<{ S: number; M: number; L: number } | null>;
  currentApiPrice: number | null;
  refetchPrice: () => Promise<void>;
  underwritingFailed: boolean;
  underwritingReason: string | null;
  handlePetDetailsSubmitAndNavigate: (
    submittedPetName: string,
    selectedBreed: string,
    submittedBreedLabel: string | null,
    submittedBirthDate: Date | undefined,
    submittedSelectedBreedType: "purebred" | "crossbreed" | "mixed",
    submittedGender: "male" | "female" | "unknown",
    submittedIsCastrated: boolean,
    submittedIsHouseCat: boolean,
    submittedSelectedDogSize: "small" | "large" | null,
    submittedPreexistingCoverage: boolean,
    submittedChipNumber: string | null
  ) => Promise<void>;
  handleProductVariantSelectionAndNavigate: (product: ProductVariant) => void;
  handlePriceCustomizationSubmitAndNavigate: (
    product: ProductVariant,
    price: number,
    preexisting: boolean,
    worldwide: boolean,
    deductible: number,
    coPayCoverage: number,
    interval: 1 | 2 | 4 | 12,
    method: "credit_card" | "ESR",
    startDate: Date
  ) => void;
  handleRequestOffer: (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    street: string,
    housenumber: string,
    city: string,
    zipCode: string,
    country: string
  ) => Promise<void>;
  handleCreateApplication: (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    street: string,
    housenumber: string,
    city: string,
    zipCode: string,
    country: string
  ) => Promise<void>;
  handleContactDetailsContinue: (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    street: string,
    housenumber: string,
    city: string,
    zipCode: string,
    country: string,
    birthdate: Date | undefined,
    gender: "male" | "female" | ""
  ) => void;
  isSubmittingOffer: boolean;
  goToHome: () => void;
  goToPetDetails: () => void;
  goToProductVariantSelection: () => void;
  goToProductCustomizationOptions: () => void;
  goToContactDetails: () => void;
  goToConfirmation: () => void;
  setSelectedProduct: (product: ProductVariant | null) => void;
  setChipNumber: (value: string | null) => void;
  setPreexistingCoverage: (checked: boolean) => void;
  setWorldwideCoverage: (checked: boolean) => void;
  setYearlyDeductible: (deductible: number) => void;
  setCoPayCoveragePercentage: (percentage: number) => void;
  setPaymentInterval: (interval: 1 | 2 | 4 | 12) => void;
  setPaymentMethod: (method: "credit_card" | "ESR") => void;
  setStartOfInsurance: (date: Date) => void;
  onPetTypeChange: (petType: "cat" | "dog") => void;
  setDispatchContract: (v: "customer" | "customer_and_agent" | "agent") => void;
  setDispatchInvoice: (v: "customer" | "customer_and_agent" | "agent") => void;
  setDispatchReminder: (v: "customer" | "customer_and_agent" | "agent") => void;
  setDispatchEmail: (v: "customer" | "customer_and_agent" | "agent") => void;
  setAgentDispatchEmail: (email: string | null) => void;
  setConsultationProtocolDocument: (documentId: number, filename: string) => void;
  clearConsultationProtocolDocument: () => void;
  setConsultationProtocolPending: (base64File: string, filename: string) => void;
  clearConsultationProtocolPending: () => void;
  setBundlePrices: (prices: { S: number; M: number; L: number } | null) => void;
}

const FunnelStepRenderer: React.FC<FunnelStepRendererProps> = ({
  funnelState,
  getLocalizedPath,
  isLoadingPrice,
  fetchInitialBundlePrices,
  currentApiPrice,
  refetchPrice,
  underwritingFailed,
  underwritingReason,
  handlePetDetailsSubmitAndNavigate,
  handleProductVariantSelectionAndNavigate,
  handlePriceCustomizationSubmitAndNavigate,
  handleRequestOffer,
  handleCreateApplication,
  handleContactDetailsContinue,
  isSubmittingOffer,
  goToHome,
  goToPetDetails,
  goToProductVariantSelection,
  goToProductCustomizationOptions,
  goToContactDetails,
  goToConfirmation,
  setSelectedProduct,
  setChipNumber,
  setPreexistingCoverage,
  setWorldwideCoverage,
  setYearlyDeductible,
  setCoPayCoveragePercentage,
  setPaymentInterval,
  setPaymentMethod,
  setStartOfInsurance,
  onPetTypeChange,
  setDispatchContract,
  setDispatchInvoice,
  setDispatchReminder,
  setDispatchEmail,
  setAgentDispatchEmail,
  setConsultationProtocolDocument,
  clearConsultationProtocolDocument,
  setConsultationProtocolPending,
  clearConsultationProtocolPending,
  setBundlePrices,
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isFetchingBundlePrices, setIsFetchingBundlePrices] = React.useState(false);

  const {
    petType,
    petName,
    breed,
    breedLabel,
    birthDate,
    selectedBreedType,
    gender,
    isCastrated,
    isHouseCat,
    selectedDogSize,
    chipNumber,
    bundlePrices,
    selectedProduct,
    finalPrice,
    contactFirstName, // Use contactFirstName directly
    contactLastName,  // Use contactLastName directly
    contactEmail,
    contactPhone,
    street,
    housenumber,
    city,
    zipCode,
    country,
    preexistingCoverage,
    worldwideCoverage,
    yearlyDeductible,
    coPayCoveragePercentage,
    paymentInterval,
    paymentMethod,
    startOfInsurance,
    isApplicationCreated,
    agentId,
    dispatchContract,
    dispatchInvoice,
    dispatchReminder,
    dispatchEmail,
    agentDispatchEmail,
    contactBirthdate,
    customerGender,
    offerLoadedFromUrl,
    hasLeftConfirmationAfterOfferLoad,
  } = funnelState;

  const calculateAge = (dob: Date): number => {
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
    return age;
  };

  React.useEffect(() => {
    const allPetDetailsAvailable = petType && petName && breed && birthDate && selectedBreedType && gender;
    const dogSizeConditionMet = petType === "cat" || selectedBreedType !== "mixed" || selectedDogSize !== null;

    const onVariantSelection = location.pathname === getLocalizedPath('routes.productVariantSelection');

    if (onVariantSelection && allPetDetailsAvailable && dogSizeConditionMet) {
      const fetchPrices = async () => {
        setIsFetchingBundlePrices(true);
        const prices = await fetchInitialBundlePrices(
          petType!,
          petName!,
          breed,
          birthDate,
          selectedBreedType!,
          gender!,
          isCastrated,
          isHouseCat,
          selectedDogSize,
          preexistingCoverage,
          "placeholder_name",
          "placeholder@email.com",
          i18n.language,
          agentId,
          worldwideCoverage,
          yearlyDeductible,
          coPayCoveragePercentage,
          paymentInterval,
          paymentMethod,
          startOfInsurance
        );
        if (prices) {
          setBundlePrices(prices);
        }
        setIsFetchingBundlePrices(false);
      };
      fetchPrices();
    }
  }, [
    location.pathname, petType, petName, breed, birthDate, selectedBreedType, gender,
    isCastrated, isHouseCat, selectedDogSize, preexistingCoverage, i18n.language, fetchInitialBundlePrices, getLocalizedPath, agentId,
    worldwideCoverage, yearlyDeductible, coPayCoveragePercentage, paymentInterval, paymentMethod, startOfInsurance, setBundlePrices
  ]);

  React.useEffect(() => {
    const onCustomization = location.pathname === getLocalizedPath('routes.productCustomization');
    if (onCustomization && selectedProduct) {
      refetchPrice();
    }
  }, [
    location.pathname, selectedProduct, preexistingCoverage, worldwideCoverage, yearlyDeductible,
    coPayCoveragePercentage, paymentInterval, paymentMethod, startOfInsurance, getLocalizedPath, refetchPrice
  ]);

  const currentPath = location.pathname;
  const shouldReuseExistingOffer = offerLoadedFromUrl && !hasLeftConfirmationAfterOfferLoad;

  if (
    currentPath === getLocalizedPath('routes.home') ||
    currentPath === getLocalizedPath('routes.petDetails')
  ) {
    return (
      <PetDetails
        petType={petType}
        onDetailsSubmit={handlePetDetailsSubmitAndNavigate}
        onBack={goToHome}
        initialPetName={petName}
        initialBreed={breed}
        initialBirthDate={birthDate}
        initialSelectedBreedType={selectedBreedType}
        initialGender={gender}
        initialIsCastrated={isCastrated}
        initialIsHouseCat={isHouseCat}
        initialSelectedDogSize={selectedDogSize}
        initialPreexistingCoverage={preexistingCoverage}
        initialChipNumber={chipNumber}
        onPetTypeChange={onPetTypeChange}
      />
    );
  } else if (currentPath === getLocalizedPath('routes.productVariantSelection')) {
    return (
      <ProductVariantSelection
        petType={petType || "dog"}
        petName={petName || t("common.yourPet")}
        breed={breed || ""}
        age={birthDate ? calculateAge(birthDate) : 0}
        initialBundlePrices={bundlePrices}
        onSelectVariantAndNavigate={handleProductVariantSelectionAndNavigate}
        onBack={goToPetDetails}
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
        isLoadingPrice={isLoadingPrice}
        paymentInterval={paymentInterval}
        isFetchingBundlePrices={isFetchingBundlePrices}
      />
    );
  } else if (currentPath === getLocalizedPath('routes.productCustomization')) {
    return (
      <ProductCustomizationOptions
        petType={petType || "dog"}
        petName={petName || t("common.yourPet")}
        breed={breed || ""}
        age={birthDate ? calculateAge(birthDate) : 0}
        onCustomizeProduct={handlePriceCustomizationSubmitAndNavigate}
        onBack={goToProductVariantSelection}
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
        preexistingCoverage={preexistingCoverage}
        setPreexistingCoverage={setPreexistingCoverage}
        worldwideCoverage={worldwideCoverage}
        setWorldwideCoverage={setWorldwideCoverage}
        yearlyDeductible={yearlyDeductible}
        setYearlyDeductible={setYearlyDeductible}
        coPayCoveragePercentage={coPayCoveragePercentage}
        setCoPayCoveragePercentage={setCoPayCoveragePercentage}
        paymentInterval={paymentInterval}
        setPaymentInterval={setPaymentInterval}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        startOfInsurance={startOfInsurance}
        setStartOfInsurance={setStartOfInsurance}
        currentApiPrice={currentApiPrice}
        isLoadingPrice={isLoadingPrice}
        underwritingFailed={underwritingFailed}
        underwritingReason={underwritingReason}
      />
    );
  } else if (currentPath === getLocalizedPath('routes.addressDetails')) {
    return (
      <ContactDetailsForm
        onContinueToSummary={handleContactDetailsContinue}
        onBack={goToProductCustomizationOptions}
        initialFirstName={contactFirstName || ""} // Pass directly
        initialLastName={contactLastName || ""}   // Pass directly
        initialEmail={contactEmail || ""}
        initialPhone={contactPhone || ""}
        initialStreet={street || ""}
        initialHousenumber={housenumber || ""}
        initialCity={city || ""}
        initialZipCode={zipCode || ""}
        initialBirthdate={contactBirthdate}
        initialGender={customerGender || ""}
        isLoading={isSubmittingOffer}
        initialDispatchContract={dispatchContract}
        initialDispatchInvoice={dispatchInvoice}
        initialDispatchReminder={dispatchReminder}
        initialDispatchEmail={dispatchEmail}
        initialAgentDispatchEmail={agentDispatchEmail || ""}
        onDispatchContractChange={setDispatchContract}
        onDispatchInvoiceChange={setDispatchInvoice}
        onDispatchReminderChange={setDispatchReminder}
        onDispatchEmailChange={setDispatchEmail}
        onAgentDispatchEmailChange={setAgentDispatchEmail}
        underwritingFailed={underwritingFailed}
      />
    );
  } else if (currentPath === getLocalizedPath('routes.confirmation')) {
    if (!selectedProduct || finalPrice === null) {
      return (
        <div className="w-full max-w-4xl mx-auto p-8 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          <div className="text-sm text-muted-foreground">Restoring your summary...</div>
        </div>
      );
    }
    return (
      <ConfirmationPage
        selectedProduct={selectedProduct!}
        finalPrice={finalPrice!}
        contactFirstName={contactFirstName} // Pass directly
        contactLastName={contactLastName}   // Pass directly
        contactEmail={contactEmail!}
        contactPhone={contactPhone}
        addressLine1={street || ""}
        addressLine2={housenumber || null}
        city={city || ""}
        zipCode={zipCode || ""}
        country={country || ""}
        petType={petType || "dog"}
        petName={petName || ""}
        breed={breed}
        breedLabel={breedLabel}
        birthDate={birthDate!}
        selectedBreedType={selectedBreedType}
        gender={gender || "unknown"}
        isCastrated={isCastrated}
        isHouseCat={isHouseCat}
        preexistingCoverage={preexistingCoverage}
        worldwideCoverage={worldwideCoverage}
        yearlyDeductible={yearlyDeductible}
        coPayCoveragePercentage={coPayCoveragePercentage}
        paymentInterval={paymentInterval}
        paymentMethod={paymentMethod}
        startOfInsurance={startOfInsurance}
        firstDue={funnelState.firstDue}
        dispatchContract={dispatchContract}
        dispatchInvoice={dispatchInvoice}
        dispatchReminder={dispatchReminder}
        dispatchEmail={dispatchEmail}
        agentDispatchEmail={agentDispatchEmail}
        contractId={funnelState.contractId}
        contractToken={funnelState.contractToken}
        consultationProtocolDocumentId={funnelState.consultationProtocolDocumentId}
        consultationProtocolFilename={funnelState.consultationProtocolFilename}
        consultationProtocolPendingFile={funnelState.consultationProtocolPendingFile}
        consultationProtocolPendingFilename={funnelState.consultationProtocolPendingFilename}
        offerLoadedFromUrl={funnelState.offerLoadedFromUrl}
        hasLeftConfirmationAfterOfferLoad={funnelState.hasLeftConfirmationAfterOfferLoad}
        onSetConsultationProtocolDocument={setConsultationProtocolDocument}
        onClearConsultationProtocolDocument={clearConsultationProtocolDocument}
        onSetConsultationProtocolPending={setConsultationProtocolPending}
        onClearConsultationProtocolPending={clearConsultationProtocolPending}
        onStartNewQuote={goToHome}
        onBack={goToContactDetails}
        onApplyOffer={() =>
          handleCreateApplication(
            contactFirstName || "",
            contactLastName || "",
            contactEmail || "",
            contactPhone || "",
            street || "",
            housenumber || "",
            city || "",
            zipCode || "",
            country || ""
          )
        }
        onCreateOffer={() =>
          handleRequestOffer(
            contactFirstName || "",
            contactLastName || "",
            contactEmail || "",
            contactPhone || "",
            street || "",
            housenumber || "",
            city || "",
            zipCode || "",
            country || ""
          )
        }
        isApplicationCreated={isApplicationCreated}
        isLoading={isSubmittingOffer}
        onEditPlanAndCoverage={goToProductVariantSelection}
        onEditPetDetails={goToPetDetails}
        onEditPersonalDetails={goToContactDetails}
        onEditPayment={goToProductCustomizationOptions}
        customerGender={funnelState.customerGender}
        contactBirthdate={contactBirthdate}
        selectedDogSize={selectedDogSize}
        chipNumber={chipNumber}
        onChipNumberChange={setChipNumber}
        hideRequestOfferButton={shouldReuseExistingOffer}
      />
    );
  }
  return (
    <PetDetails
      petType={petType}
      onDetailsSubmit={handlePetDetailsSubmitAndNavigate}
      onBack={goToHome}
      initialPetName={petName}
      initialBreed={breed}
      initialBirthDate={birthDate}
      initialSelectedBreedType={selectedBreedType}
      initialGender={gender}
      initialIsCastrated={isCastrated}
      initialIsHouseCat={isHouseCat}
      initialSelectedDogSize={selectedDogSize}
      initialPreexistingCoverage={preexistingCoverage}
      initialChipNumber={chipNumber}
      onPetTypeChange={onPetTypeChange}
    />
  );
};

export default FunnelStepRenderer;
